import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import { Rewards } from './Rewards/index.tsx';
import type { AuctionNotification_auctionNotification } from './__generated__/index.graphql.ts';
import { useAuctionLink } from './useAuctionLink.ts';

type Props = CommonNotificationProps & {
  notification: AuctionNotification_auctionNotification;
};

export const AuctionNotification = ({ notification, ...rest }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { name, createdAt, tokenAuction, rewards, sport, read } = notification;
  const makeAuctionLink = useAuctionLink();

  const link = makeAuctionLink(tokenAuction);

  const titleNode = (
    <>
      <LazyMarkdown data={notification.markdownBody} />
      {name === 'card_bought' && <Rewards rewards={rewards} />}
    </>
  );

  const card = tokenAuction.cards.at(0);

  return (
    <DumbNotification
      name={name}
      title={titleNode}
      userAvatar={currentUser}
      cardPicture={!!card && <Card card={card} />}
      link={link}
      createdAt={createdAt}
      sport={sport}
      read={read}
      {...rest}
    />
  );
};

AuctionNotification.fragments = {
  auctionNotification: gql`
    fragment AuctionNotification_auctionNotification on AuctionNotification {
      id
      ...Notification_notificationInterface
      tokenAuction {
        id
        ...AuctionLink_auction
        cards: anyCards {
          slug
          sport
          ...Card_anyCard
        }
      }
      rewards {
        id
        ...Rewards_anyRewardInterface
      }
    }
    ${useAuctionLink.fragments.auctionLink}
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
    ${Rewards.fragments.anyReward}
  ` as TypedDocumentNode<AuctionNotification_auctionNotification>,
};
